import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../../../shared/components/ui/Button';
import { addFollowUp, updateFollowUpDate } from '../../services/api/implementation/impl';
import {
  buildScheduleDateOptions,
  buildScheduleTimeSlots,
  toScheduledAtIso
} from './aiScheduleTime';

const BASE_LOG_OPTIONS = [
  'Follow-up Tomorrow',
  'Follow-up in 3 Days',
  'Follow-up Next Week',
  'Follow-up in 15 Days',
  'Follow-up After Site Visit',
  'Follow-up After Family Discussion',
  'Call Connected (Meeting Scheduled)',
  'Call Connected (Interested)',
  'Wants Site Visit',
  'Site Visit Done (Awaiting Feedback)'
];

function buildLogOptions(suggestedLog) {
  if (!suggestedLog || BASE_LOG_OPTIONS.includes(suggestedLog)) {
    return BASE_LOG_OPTIONS;
  }
  return [suggestedLog, ...BASE_LOG_OPTIONS];
}

export default function AiFollowUpFromInsightModal({
  open,
  onClose,
  leadId,
  scheduleSuggestion,
  currentStage = 'Approached',
  hasActiveFollowUp = false,
  followUpId = null,
  onSuccess
}) {
  const dateOptions = useMemo(() => buildScheduleDateOptions(90), []);
  const timeSlots = useMemo(() => buildScheduleTimeSlots(), []);

  const logOptions = useMemo(
    () => buildLogOptions(scheduleSuggestion?.suggestedFollowUpLog),
    [scheduleSuggestion?.suggestedFollowUpLog]
  );

  const initial = useMemo(() => {
    if (scheduleSuggestion?.suggestedDate && scheduleSuggestion?.suggestedTime) {
      return {
        date: scheduleSuggestion.suggestedDate,
        time: scheduleSuggestion.suggestedTime,
        agenda: scheduleSuggestion.suggestedAgenda || '',
        log: scheduleSuggestion.suggestedFollowUpLog || 'Follow-up in 3 Days'
      };
    }
    return {
      date: dateOptions[0],
      time: '11:00',
      agenda: '',
      log: 'Follow-up in 3 Days'
    };
  }, [scheduleSuggestion, dateOptions]);

  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [agenda, setAgenda] = useState(initial.agenda);
  const [log, setLog] = useState(initial.log);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (open) {
      setDate(initial.date);
      setTime(initial.time);
      setAgenda(initial.agenda);
      setLog(initial.log);
    }
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!agenda.trim()) {
      toast.error('Enter agenda');
      return;
    }
    const nextFollowUpDate = toScheduledAtIso(date, time);
    setSubmitting(true);
    try {
      if (hasActiveFollowUp && followUpId) {
        await updateFollowUpDate(followUpId, {
          nextFollowUpDate,
          agenda: agenda.trim(),
          log
        });
      } else {
        await addFollowUp({
          agenda: agenda.trim(),
          nextFollowUpDate,
          log,
          lead_id: leadId,
          status: 'Active',
          name: currentStage
        });
      }
      toast.success(hasActiveFollowUp ? 'Follow-up rescheduled' : 'Follow-up scheduled');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to schedule follow-up');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Schedule follow-up</h3>
            <p className="text-xs text-gray-500">From AI recommendation</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {scheduleSuggestion?.reason ? (
            <p className="rounded-xl border border-teal-100 bg-teal-50/60 px-3 py-2 text-xs leading-relaxed text-teal-950">
              {scheduleSuggestion.reason}
            </p>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                Date
              </label>
              <select
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                {dateOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                Time
              </label>
              <select
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                {timeSlots.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Log</label>
            <select
              value={log}
              onChange={(e) => setLog(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {logOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Agenda</label>
            <textarea
              value={agenda}
              onChange={(e) => setAgenda(e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
              placeholder="What to cover on the follow-up call…"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-4">
          <Button displayType="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Schedule follow-up'}
          </Button>
        </div>
      </div>
    </div>
  );
}
